import { useEffect, useState } from 'react';

import { LocalStorage } from '@/enums';

export type FeatureFlagKey = 'showDataExplorer' | 'showSurveys';

export type FeatureFlagsState = Record<FeatureFlagKey, boolean>;

const defaults: FeatureFlagsState = {
  showDataExplorer: false,
  showSurveys: false,
};

const listeners = new Set<() => void>();

function notify() {
  listeners.forEach(listener => listener());
}

function parseStored(raw: string | null): FeatureFlagsState {
  if (!raw) {
    return { ...defaults };
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return { ...defaults };
    }

    const parsedRecord = parsed as Partial<Record<FeatureFlagKey, unknown>>;
    const state = { ...defaults };

    (Object.keys(defaults) as FeatureFlagKey[]).forEach(key => {
      if (typeof parsedRecord[key] === 'boolean') {
        state[key] = parsedRecord[key];
      }
    });

    return state;
  } catch {
    return { ...defaults };
  }
}

function readFromStorage(): FeatureFlagsState {
  try {
    return parseStored(localStorage.getItem(LocalStorage.FEATURE_FLAGS));
  } catch {
    return { ...defaults };
  }
}

function persist(state: FeatureFlagsState): void {
  localStorage.setItem(LocalStorage.FEATURE_FLAGS, JSON.stringify(state));
}

let cache: FeatureFlagsState = readFromStorage();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useFeatureFlags(): FeatureFlagsState {
  const [state, setState] = useState<FeatureFlagsState>(() => ({ ...cache }));

  useEffect(() => {
    return subscribe(() => {
      setState({ ...cache });
    });
  }, []);

  return state;
}

export function setFeatureFlag(key: FeatureFlagKey, value: boolean): void {
  if (cache[key] === value) {
    return;
  }
  cache = { ...cache, [key]: value };
  persist(cache);
  notify();
}

export function getFeatureFlag(key: FeatureFlagKey): boolean {
  return cache[key];
}

export function getAllFeatureFlags(): FeatureFlagsState {
  return { ...cache };
}

export type WindowFeatureFlagsApi = {
  set: (key: FeatureFlagKey, value: boolean) => void;
  get: (key: FeatureFlagKey) => boolean;
  getAll: () => FeatureFlagsState;
};

export function initWindowFeatureFlags(): void {
  cache = readFromStorage();

  const api: WindowFeatureFlagsApi = {
    set: setFeatureFlag,
    get: getFeatureFlag,
    getAll: getAllFeatureFlags,
  };

  window.featureFlags = api;

  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key !== LocalStorage.FEATURE_FLAGS || event.storageArea !== localStorage) {
      return;
    }
    cache = readFromStorage();
    notify();
  });
}

declare global {
  interface Window {
    featureFlags?: WindowFeatureFlagsApi;
  }
}
